/**
 * Utility functions for UK Postcode and UK Telephone Number validation and formatting.
 */

/**
 * Standard UK Postcode regex pattern.
 * Supports official UK postcode formats (with optional space, case-insensitive).
 * Valid formats: A9 9AA, A99 9AA, AA9 9AA, AA99 9AA, A9A 9AA, AA9A 9AA, GIR 0AA
 */
const UK_POSTCODE_REGEX = /^(GIR\s?0AA|[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2})$/i;

/**
 * Validates whether a string is a valid UK postcode.
 *
 * @param {string} value - The postcode string to validate
 * @returns {boolean} boolean indicating if the postcode is valid
 */
export function validateUKPostcode(value) {
  if (!value || typeof value !== "string") {
    return false;
  }
  const trimmed = value.trim();
  return UK_POSTCODE_REGEX.test(trimmed);
}

/**
 * Formats a UK postcode to standard uppercase with a single space before the final 3 characters.
 * E.g., "sw1a1aa" -> "SW1A 1AA", "m11ae" -> "M1 1AE".
 *
 * @param {string} value - The postcode string to format
 * @returns {string} formatted uppercase postcode string
 */
export function formatUKPostcode(value) {
  if (!value || typeof value !== "string") {
    return "";
  }
  const cleaned = value.trim().toUpperCase().replace(/\s+/g, "");
  if (cleaned.length > 3) {
    const outcode = cleaned.slice(0, -3);
    const incode = cleaned.slice(-3);
    return `${outcode} ${incode}`;
  }
  return cleaned;
}

/**
 * Strips non-digit characters and normalizes international UK prefixes (+44 / 0044) to leading 0.
 *
 * @param {string} value - Telephone input string
 * @returns {string | null} cleaned digit string starting with 0, or null if invalid characters exist
 */
function getCleanUKTelephone(value) {
  if (!value || typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  // Reject any string containing alphabetic characters
  if (/[a-zA-Z]/.test(trimmed)) {
    return null;
  }

  // Remove spaces, hyphens, dashes, dots, parentheses
  let cleaned = trimmed.replace(/[\s\-\(\)\.]/g, "");

  // Convert +44 or 0044 prefix to standard leading 0
  if (cleaned.startsWith("+44")) {
    cleaned = "0" + cleaned.slice(3);
  } else if (cleaned.startsWith("0044")) {
    cleaned = "0" + cleaned.slice(4);
  }

  return cleaned;
}

/**
 * Validates whether a string is a valid UK telephone number.
 * Supports UK landlines (01xx, 02xx), mobile numbers (07xxx), and non-geographic numbers (03xx, 08xx).
 * Also supports international +44 format.
 *
 * @param {string} value - The telephone number string to validate
 * @returns {boolean} boolean indicating if the phone number is valid
 */
export function validateUKTelephone(value) {
  const cleaned = getCleanUKTelephone(value);
  if (!cleaned) {
    return false;
  }

  // Standard UK telephone numbers start with 01, 02, 03, 07, 08, 09 and are 10 or 11 digits long
  const UK_PHONE_REGEX = /^0[123789]\d{8,9}$/;
  return UK_PHONE_REGEX.test(cleaned);
}

/**
 * Normalizes a UK telephone number string by trimming leading/trailing whitespace
 * and compressing multiple consecutive spaces into a single space.
 *
 * @param {string} value - The telephone number string to normalize
 * @returns {string} normalized telephone string
 */
export function normalizeUKTelephone(value) {
  if (!value || typeof value !== "string") {
    return "";
  }
  return value.trim().replace(/\s+/g, " ");
}
